import { nanoid } from 'nanoid';
import type { AudioAsset, OutlineBeat, Settings, Slug, Story, Vibe } from '../types';
import { VIBE_GUIDANCE, VIBE_LABELS } from '../types';
import { interpolate } from './interpolate';

function slugToneVars(slug: Slug): {
  vibe: string;
  vibe_guidance: string;
  guidance_block: string;
} {
  const vibeLabel = slug.vibe ? VIBE_LABELS[slug.vibe] : '';
  const vibeGuidance = slug.vibe ? VIBE_GUIDANCE[slug.vibe] : '';
  const userGuidance = slug.guidance?.trim();
  const guidance_block = userGuidance
    ? `## Additional tonal guidance from this batch\n${userGuidance}`
    : '';
  return { vibe: vibeLabel, vibe_guidance: vibeGuidance, guidance_block };
}
import { generateText, streamText } from './anthropic';
import { synthesize } from './elevenlabs';
import { parseOutlineBeats, parseSlugs } from './slugParse';
import { getTemplate } from '../templates/registry';
import { getElevenVoiceId } from './credentials';
import { outlines as outlineStore, prose as proseStore } from '../storage/db';
import { useAppStore } from '../stores/useAppStore';
import { headBlob, isSyncConfigured } from '../lib/syncClient';

const inflight = new Map<string, AbortController>();

export function abortStory(storyId: string) {
  const ac = inflight.get(storyId);
  if (ac) {
    ac.abort();
    inflight.delete(storyId);
  }
}

function claimController(storyId: string): AbortController {
  abortStory(storyId);
  const ac = new AbortController();
  inflight.set(storyId, ac);
  return ac;
}

function release(storyId: string, ac: AbortController) {
  if (inflight.get(storyId) === ac) inflight.delete(storyId);
}

function buildSystem(settings: Settings, universeId: string) {
  const bible = getTemplate('story_bible', universeId);
  const voice = getTemplate('voice_config', universeId);
  const universe = useAppStore.getState().universes[universeId];
  const canonText = universe?.canon?.trim();
  const blocks: { type: 'text'; text: string; cache?: boolean }[] = [
    { type: 'text', text: bible, cache: true },
    { type: 'text', text: voice, cache: true },
  ];
  if (canonText) {
    blocks.push({
      type: 'text',
      text: `## Established canon (facts committed in prior stories)\n\n${canonText}`,
      cache: true,
    });
  }
  blocks.push({
    type: 'text',
    text: `You are helping generate stories within this universe. Target length: ${settings.targetWords} words when asked for prose.`,
  });
  return blocks;
}

function resolveVoiceId(story: Story, settings: Settings): string | undefined {
  if (story.voiceIdOverride?.trim()) return story.voiceIdOverride.trim();
  const universe = useAppStore.getState().universes[story.universeId];
  if (universe?.defaultVoiceId?.trim()) return universe.defaultVoiceId.trim();
  return getElevenVoiceId(settings);
}

export async function generateSlugBatch(args: {
  settings: Settings;
  universeId: string;
  protagonists: string;
  vibe: Vibe;
  guidance?: string;
  seenTitles?: string[];
}): Promise<Slug[]> {
  const seen = (args.seenTitles ?? []).filter((t) => t.trim());
  const seen_block = seen.length
    ? `## Already generated — do NOT repeat these premises or near-duplicates\n${seen.map((t) => `- ${t}`).join('\n')}`
    : '';
  const guidance = args.guidance?.trim();
  const guidance_block = guidance
    ? `## Additional guidance for this batch\n${guidance}`
    : '';
  const tpl = getTemplate('slug_batch', args.universeId);
  const userText = interpolate(tpl, {
    protagonists: args.protagonists,
    vibe: VIBE_LABELS[args.vibe],
    vibe_guidance: VIBE_GUIDANCE[args.vibe],
    guidance_block,
    seen_block,
  });
  const ac = new AbortController();
  const raw = await generateText({
    model: args.settings.model,
    systemBlocks: buildSystem(args.settings, args.universeId),
    userText,
    maxTokens: 1500,
    signal: ac.signal,
  });
  return parseSlugs(raw, nanoid(8), args.universeId, args.vibe, guidance);
}

export async function generateOutline(args: {
  storyId: string;
  universeId: string;
  settings: Settings;
  slug: Slug;
}): Promise<{ raw: string; beats: OutlineBeat[] }> {
  const ac = claimController(args.storyId);
  try {
    const tpl = getTemplate('outline_gen', args.universeId);
    const userText = interpolate(tpl, {
      slug_title: args.slug.title,
      slug_hook: args.slug.hook,
      ...slugToneVars(args.slug),
    });
    const raw = await generateText({
      model: args.settings.model,
      systemBlocks: buildSystem(args.settings, args.universeId),
      userText,
      maxTokens: 2000,
      signal: ac.signal,
    });
    const parsed = parseOutlineBeats(raw);
    const beats: OutlineBeat[] = parsed.map((b) => ({
      id: nanoid(8),
      heading: b.heading,
      body: b.body,
    }));
    return { raw, beats };
  } finally {
    release(args.storyId, ac);
  }
}

export async function reviseOutline(args: {
  storyId: string;
  universeId: string;
  settings: Settings;
  slug: Slug;
  currentOutline: string;
  notes: string;
}): Promise<{ raw: string; beats: OutlineBeat[] }> {
  const ac = claimController(args.storyId);
  try {
    const tpl = getTemplate('outline_revise', args.universeId);
    const userText = interpolate(tpl, {
      outline: args.currentOutline,
      revision_notes: args.notes,
      ...slugToneVars(args.slug),
    });
    const raw = await generateText({
      model: args.settings.model,
      systemBlocks: buildSystem(args.settings, args.universeId),
      userText,
      maxTokens: 2000,
      signal: ac.signal,
    });
    const parsed = parseOutlineBeats(raw);
    return {
      raw,
      beats: parsed.map((b) => ({ id: nanoid(8), heading: b.heading, body: b.body })),
    };
  } finally {
    release(args.storyId, ac);
  }
}

export async function generateProse(args: {
  storyId: string;
  universeId: string;
  settings: Settings;
  outline: string;
  slug: Slug;
  onChunk: (text: string) => void;
}): Promise<string> {
  const ac = claimController(args.storyId);
  try {
    const tpl = getTemplate('prose_gen', args.universeId);
    const userText = interpolate(tpl, {
      outline: args.outline,
      slug_title: args.slug.title,
      slug_hook: args.slug.hook,
      target_words: String(args.settings.targetWords),
      ...slugToneVars(args.slug),
    });
    return await streamText(
      {
        model: args.settings.model,
        systemBlocks: buildSystem(args.settings, args.universeId),
        userText,
        maxTokens: 4000,
        signal: ac.signal,
        persistAs: `prose_${args.storyId}`,
      },
      { onChunk: args.onChunk }
    );
  } finally {
    release(args.storyId, ac);
  }
}

export async function reviseProse(args: {
  storyId: string;
  universeId: string;
  settings: Settings;
  slug: Slug;
  currentProse: string;
  notes: string;
  onChunk: (text: string) => void;
}): Promise<string> {
  const ac = claimController(args.storyId);
  try {
    const tpl = getTemplate('prose_revise', args.universeId);
    const userText = interpolate(tpl, {
      prose: args.currentProse,
      revision_notes: args.notes,
      target_words: String(args.settings.targetWords),
      ...slugToneVars(args.slug),
    });
    return await streamText(
      {
        model: args.settings.model,
        systemBlocks: buildSystem(args.settings, args.universeId),
        userText,
        maxTokens: 4000,
        signal: ac.signal,
        persistAs: `prose_${args.storyId}`,
      },
      { onChunk: args.onChunk }
    );
  } finally {
    release(args.storyId, ac);
  }
}

export function formatTtsScript(title: string, prose: string): string {
  const cleanTitle = title.trim().replace(/[.!?]+$/, '');
  return `${cleanTitle}.\n\n<break time="1.2s" />\n\n${prose.trim()}`;
}

export async function generateAudio(args: {
  story: Story;
  settings: Settings;
  prose: string;
  title?: string;
}): Promise<{ key: string; size: number; mime: string; voiceId: string; reused: boolean }> {
  const voiceId = resolveVoiceId(args.story, args.settings);
  if (!voiceId) throw new Error('No voice selected (universe default or override)');
  const ac = claimController(args.story.id);
  const blobKey = `audio_${args.story.id}`;
  try {
    if (isSyncConfigured()) {
      try {
        const existing = await headBlob(blobKey);
        if (existing) {
          return {
            key: blobKey,
            size: existing.size,
            mime: existing.mime || 'audio/mpeg',
            voiceId,
            reused: true,
          };
        }
      } catch {
        // headBlob failure is non-fatal — fall through to a full synth.
      }
    }
    const text = args.title ? formatTtsScript(args.title, args.prose) : args.prose;
    const result = await synthesize({
      key: blobKey,
      voiceId,
      modelId: args.settings.ttsModel,
      text,
      signal: ac.signal,
    });
    return { ...result, voiceId, reused: false };
  } finally {
    release(args.story.id, ac);
  }
}

export function newStoryFromSlug(slug: Slug): Story {
  const now = Date.now();
  return {
    id: nanoid(10),
    universeId: slug.universeId,
    stage: 'slug',
    slug,
    revisionLog: [],
    createdAt: now,
    updatedAt: now,
  };
}

export function outlineToText(beats: OutlineBeat[]): string {
  return beats.map((b, i) => `${i + 1}. ${b.heading}\n${b.body}`).join('\n\n');
}

export async function extractCanon(args: {
  universeId: string;
  settings: Settings;
  newProse: string;
}): Promise<string> {
  const universe = useAppStore.getState().universes[args.universeId];
  if (!universe) throw new Error('Universe not found');

  const tpl = getTemplate('canon_update', args.universeId);
  const userText = interpolate(tpl, {
    current_canon: universe.canon?.trim() || '(empty — no canon yet)',
    new_prose: args.newProse,
  });

  const bible = getTemplate('story_bible', args.universeId);
  const ac = new AbortController();
  const raw = await generateText({
    model: args.settings.model,
    systemBlocks: [{ type: 'text', text: bible, cache: true }],
    userText,
    maxTokens: 4000,
    signal: ac.signal,
    timeoutMs: 240_000,
    label: 'canon',
  });
  return raw.trim();
}

export async function backgroundExtractCanon(
  universeId: string,
  settings: Settings,
  newProse: string,
  sourceStoryId?: string
): Promise<void> {
  const store = useAppStore.getState();
  store.setUniverseCanonInFlight(universeId, true);
  try {
    const canon = await extractCanon({ universeId, settings, newProse });
    if (canon) {
      useAppStore.getState().setUniverseCanon(universeId, canon, sourceStoryId);
    }
  } catch (e) {
    console.warn('canon extraction failed', e);
  } finally {
    useAppStore.getState().setUniverseCanonInFlight(universeId, false);
  }
}

export async function runAutoPipeline(args: {
  story: Story;
  settings: Settings;
}): Promise<void> {
  const { story, settings } = args;
  const getCurrent = (): Story =>
    useAppStore.getState().stories[story.id] ?? story;

  // Outline — skip if already saved.
  let outlineRaw: string;
  const cur1 = getCurrent();
  if (cur1.outlineText) {
    outlineRaw = cur1.outlineText;
  } else {
    useAppStore.getState().setInFlight(story.id, 'outline');
    try {
      const { raw, beats } = await generateOutline({
        storyId: story.id,
        universeId: story.universeId,
        settings,
        slug: story.slug,
      });
      outlineRaw = raw;
      await outlineStore.set(story.id, raw);
      useAppStore.getState().setStoryOutline(story.id, beats, raw);
    } finally {
      useAppStore.getState().setInFlight(story.id, undefined);
    }
  }

  // Prose — skip if already saved (and the IDB blob is still present).
  let proseFull: string;
  let proseNewlyGenerated = false;
  const cur2 = getCurrent();
  const existingProse = cur2.proseKey ? await proseStore.get(story.id) : undefined;
  if (existingProse) {
    proseFull = existingProse;
  } else {
    useAppStore.getState().setInFlight(story.id, 'prose');
    try {
      proseFull = await generateProse({
        storyId: story.id,
        universeId: story.universeId,
        settings,
        outline: outlineRaw,
        slug: story.slug,
        onChunk: () => {},
      });
      await proseStore.set(story.id, proseFull);
      useAppStore.getState().setStoryProseKey(story.id, story.id);
      proseNewlyGenerated = true;
    } finally {
      useAppStore.getState().setInFlight(story.id, undefined);
    }
  }

  if (proseNewlyGenerated) {
    void backgroundExtractCanon(story.universeId, settings, proseFull, story.id);
  }

  // Audio — skip if already saved.
  const cur3 = getCurrent();
  if (!cur3.audio) {
    useAppStore.getState().setInFlight(story.id, 'tts');
    try {
      const { mime, voiceId } = await generateAudio({
        story: getCurrent(),
        settings,
        prose: proseFull,
        title: story.slug.title,
      });
      const asset: AudioAsset = {
        id: story.id,
        blobKey: story.id,
        mimeType: mime || 'audio/mpeg',
        voiceId,
        createdAt: Date.now(),
      };
      useAppStore.getState().setStoryAudio(story.id, asset);
    } finally {
      useAppStore.getState().setInFlight(story.id, undefined);
    }
  }
}
